import { MAX_INTENTS, intentLabels } from './intents';
import { useIntents } from './useIntents';

interface IntentPickerProps {
  /** What this surface calls the question — "What is this post for?". */
  legend: string;
  /** One line under the legend, when the surface has something to add. */
  hint?: string;
  /** The chosen keys, in pick order. The first is the primary. */
  value: readonly string[];
  onChange: (next: string[]) => void;
  /** How many can be on at once. Two is the server's cap and the default. */
  max?: number;
  disabled?: boolean;
  /**
   * Quiet mode for a dense surface (the brief's Fine-tune): no "let the model
   * choose" line when nothing is picked, and the picked ones are not spelled
   * out again under the chips (their blurbs stay as the chips' tooltips).
   */
  quiet?: boolean;
}

/**
 * The purpose of a post, chosen from the server's closed vocabulary — up to
 * two, in order.
 *
 * Order is the whole point of allowing a second one, so it is stated rather
 * than implied: the chips carry a 1 and a 2, the chosen ones spell themselves
 * out underneath, and a two-intent pick says what the pair does to the drafts
 * (variant 1 leads with the first, variant 2 with the second). Picking nothing
 * is a real answer and says so — "Let the model choose" is what the pipeline
 * did before this field existed.
 *
 * At the cap the unpicked chips go inert rather than silently swallowing a
 * click or quietly evicting an earlier choice; the line under them says which
 * it is. Real checkboxes throughout, so keyboard and screen-reader behaviour
 * comes for free, and a `title` puts each recipe within reach before it is
 * picked.
 *
 * The list is never hardcoded: it is `GET /intents`, cached per session. A
 * registry that cannot be read leaves the field out of the way entirely — the
 * brief still submits, and the model still decides.
 */
export function IntentPicker({
  legend,
  hint,
  value,
  onChange,
  max = MAX_INTENTS,
  disabled = false,
  quiet = false,
}: IntentPickerProps) {
  const { intents, isLoading, error } = useIntents();
  const atCap = value.length >= max;

  function toggle(key: string) {
    if (value.includes(key)) {
      onChange(value.filter((chosen) => chosen !== key));
      return;
    }
    if (atCap) return;
    onChange([...value, key]);
  }

  if (isLoading && intents.length === 0) {
    return (
      <fieldset className="chip-group intent-picker">
        <legend className="field__label">{legend}</legend>
        <p className="kb-section__status" role="status">
          Loading the list of purposes
        </p>
      </fieldset>
    );
  }

  if (intents.length === 0) {
    return (
      <fieldset className="chip-group intent-picker">
        <legend className="field__label">{legend}</legend>
        <p className="field__hint">
          {error ?? 'No purposes added yet.'} We'll pick the angle for this one.
        </p>
      </fieldset>
    );
  }

  // Pick order, not registry order: the order is the meaning here.
  const chosen = value
    .map((key) => intents.find((intent) => intent.key === key))
    .filter((intent) => intent !== undefined);
  const chosenLabels = intentLabels(value, intents);

  return (
    <fieldset className="chip-group intent-picker" disabled={disabled}>
      <legend className="field__label">{legend}</legend>
      {hint ? <p className="field__hint">{hint}</p> : null}

      <div className="chip-group__options">
        {intents.map((intent) => {
          const order = value.indexOf(intent.key);
          const isSelected = order >= 0;
          const className = isSelected
            ? 'chip chip--selected intent-chip'
            : atCap
              ? 'chip intent-chip intent-chip--inert'
              : 'chip intent-chip';

          return (
            <label className={className} key={intent.key} title={intent.blurb}>
              <input
                className="chip__input"
                type="checkbox"
                value={intent.key}
                checked={isSelected}
                disabled={!isSelected && atCap}
                onChange={() => toggle(intent.key)}
              />
              <span>{intent.label}</span>
              {/* The pick order, shown only where a second pick is possible —
                  on a one-intent picker the badge would be noise. */}
              {isSelected && max > 1 ? (
                <span className="intent-chip__order" aria-label={`Pick ${order + 1}`}>
                  {order + 1}
                </span>
              ) : null}
            </label>
          );
        })}
      </div>

      {quiet ? null : chosen.length === 0 ? (
        <p className="field__hint intent-picker__empty">
          Leave it to us. We'll pick an angle from the topic and your brand profile.
        </p>
      ) : (
        <ul className="intent-picker__chosen">
          {chosen.map((intent, position) => (
            <li className="intent-picker__chosen-item" key={intent.key}>
              <span className="intent-picker__chosen-label">
                {position + 1}. {intent.label}
              </span>{' '}
              <span className="intent-picker__chosen-blurb">{intent.blurb}</span>
            </li>
          ))}
        </ul>
      )}

      {chosen.length >= 2 ? (
        <p className="field__hint intent-picker__split">
          Version A leads with {chosenLabels[0]}, version B with {chosenLabels[1]}. Untick one to
          change the order.
        </p>
      ) : null}
    </fieldset>
  );
}
